/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, Grid, Popover, Stack } from '@mui/material'
import AdministrationAction from 'administration/actions/AdministrationAction'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import ProgressCard from 'components/card/ProgressCard'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import { isEmpty, isNil, orderBy, range, xor } from 'lodash'
import VariousMaterielAction from 'materiel/components/variousMateriel/actions/VariousMaterielAction'
import DtoVariousMaterielType from 'materiel/components/variousMateriel/dto/DtoVariousMaterielType'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import UsageAction from 'referencial/components/usage/actions/UsageAction'
import DtoUsageAgri from 'referencial/components/usage/dto/DtoUsageAgri'
import i18n from 'simple-react-i18n'
import { hasValue } from 'utils/NumberUtil'
import Select from 'components/forms/Select'
import { ENTRY_SETTINGS_FREQUENCY } from 'agriAdministration/constants/AgriConstants'
import useTitle from 'utils/customHook/useTitle'
import useStateAppSetting from 'utils/customHook/useStateAppSetting'
import useActions from 'utils/customHook/useActions'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import { getDayList } from 'utils/DateUtil'
import { getEntryFrequencyList } from 'exploitations/utils/AgriUtils'
import SimpleCheckbox from 'components/forms/SimpleCheckbox'
import { booleanParser, intParser } from 'utils/customHook/useApplicationSetting'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import { CardTitle } from 'components/card/NewCard'
import { usedUrl } from 'conf/basepath'
import NumberField from 'components/forms/NumberField'
import useGlobalParameter from 'utils/customHook/useGlobalParameter'
import SimpleInput, { INPUT_TYPES } from 'components/forms/SimpleInput'
import TerritorySettingsEntryRange from './TerritorySettingsEntryRange'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import StationAction from 'station/actions/StationAction'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'

const TerritorySettingsParamsApp = () => {
    const [readMode, setReadMode] = useState(true)
    const [SPIHackClick, setSPIHackClick] = useState(0)
    const [KCHackClick, setKCHackClick] = useState(0)

    const MODEL_OPTIONS = [
        { value: 'CLIMATO_MODEL', label: i18n.climato },
        { value: 'LSTM_MODEL', label: i18n.LSTM },
    ]
    const {
        usages,
        variousMaterielTypes,
    } = useSelector(store => ({
        usages: store.UsageReducer.usages,
        variousMaterielTypes: store.VariousMaterielReducer.variousMaterielTypes,
    }), shallowEqual)

    const dispatch = useDispatch()

    const { isLoaded, progress } = useProgressDispatch(() => [
        dispatch(VariousMaterielAction.fetchVariousMaterielTypes()),
        dispatch(AdministrationAction.fetchApplicationSettings()),
        dispatch(UsageAction.fetchUsages()),
    ], [])

    const nbMoisDefault = useGlobalParameter('A3P_nb_mois', v => v ? Number(v) : undefined)
    const errModeDefault = useGlobalParameter('A3P_err_mode')
    const nbAnneeDefault = useGlobalParameter('A3P_nb_annee', v => v ? Number(v) : undefined)
    const quantileCalculationDefault = useGlobalParameter('A3P_quantile_calculation', v => v ? Number(v) : undefined)
    const droughtCalculationDefault = useGlobalParameter('A3P_drought_calculation')
    const modeDefault = useGlobalParameter('A3P_mode')
    const dayFormatDefault = useGlobalParameter('A3P_day_format')
    const modelDefault = useGlobalParameter('A3P_model')

    const [usagesInTerritoryCalcul, setUsagesInTerritoryCalcul] = useStateAppSetting('usagesInTerritoryCalcul', v => v?.split(' ')?.filter((uId) => uId !== ''), [])
    const [nbRotationsCultMax, setNbRotationsCultMax] = useStateAppSetting('nbRotationsCultMax', v => v, 6)
    const [lowWaterStartDate, setLowWaterStartDate] = useStateAppSetting('lowWaterStartDate', v => Number(v))
    const [lowWaterEndDate, setLowWaterEndDate] = useStateAppSetting('lowWaterEndDate', v => Number(v))
    const [materielsTypeForIndex, setMaterielsTypeForIndex] = useStateAppSetting('materielsTypeForIndex', v => v?.split(',')?.map((id) => Number(id)), [])
    const [entryFrequency, setEntryFrequency] = useStateAppSetting('entryFrequency', intParser)
    const [entryFrequencyDay, setEntryFrequencyDay] = useStateAppSetting('entryFrequencyDay', intParser)
    const [entryRangeDaysBefore, setEntryRangeDaysBefore] = useStateAppSetting('entryRangeDaysBefore', intParser, 0)
    const [entryRangeDaysAfter, setEntryRangeDaysAfter] = useStateAppSetting('entryRangeDaysAfter', intParser, 0)
    const [blockConsumptionsDeletion, setBlockConsumptionsDeletion] = useStateAppSetting('blockConsumptionsDeletion', booleanParser)
    const [blockEntryDate, setBlockEntryDate] = useStateAppSetting('blockEntryDate', booleanParser)
    const [blockUnvalidEntry, setBlockUnvalidEntry] = useStateAppSetting('blockUnvalidEntry', booleanParser)
    const [showClimateControlScenarioPanel, setShowClimateControlScenarioPanel] = useStateAppSetting('showClimateControlScenarioPanel', booleanParser)
    const [situationCardPiezo, setSituationCardPiezo] = useStateAppSetting('A3PSituationCardPiezo', intParser)
    const [situationCardHydro, setSituationCardHydro] = useStateAppSetting('A3PSituationCardHydro', intParser)
    const [restrictedVPLevelLabel, setRestrictedVPLevelLabel] = useStateAppSetting('restrictedVPLevelLabel', v => v, i18n.VPRestricted)
    const [restrictedVPLevelValue, setRestrictedVPLevelValue] = useStateAppSetting('restrictedVPLevelValue', intParser)
    const [normalVPLevelLabel, setNormalVPLevelLabel] = useStateAppSetting('normalVPLevelLabel', v => v, i18n.VPNormal)
    const [normalVPLevelValue, setNormalVPLevelValue] = useStateAppSetting('normalVPLevelValue', intParser)
    const [highVPLevelLabel, setHighVPLevelLabel] = useStateAppSetting('highVPLevelLabel', v => v, i18n.VPHigh)
    const [highVPLevelValue, setHighVPLevelValue] = useStateAppSetting('highVPLevelValue', intParser)
    const [veryHighVPLevelLabel, setVeryHighVPLevelLabel] = useStateAppSetting('veryHighVPLevelLabel', v => v, i18n.VPVeryHigh)
    const [veryHighVPLevelValue, setVeryHighVPLevelValue] = useStateAppSetting('veryHighVPLevelValue', intParser)
    const [nbMois, setNbMois] = useState(nbMoisDefault)
    const [errMode, setErrMode] = useState(errModeDefault)
    const [nbAnnee, setNbAnnee] = useState(nbAnneeDefault)
    const [quantileCalculation, setQuantileCalculation] = useState(quantileCalculationDefault)
    const [droughtCalculation, setDroughtCalculation] = useState(droughtCalculationDefault)
    const [mode, setMode] = useState(modeDefault)
    const [dayFormat, setDayFormat] = useState(dayFormatDefault)
    const [model, setModel] = useState(modelDefault)
    const [anchorPopover, setAnchorPopover] = useState(null)
    const [piezoMapSituations, setPiezoMapSituations] = useState([])
    const [hydroMapSituations, setHydroMapSituations] = useState([])

    useEffect(() => {
        StationAction.promiseMapSituations(STATION_TYPE_NAME.piezometry).then(ms => setPiezoMapSituations(ms))
        StationAction.promiseMapSituations(STATION_TYPE_NAME.hydrometry).then(ms => setHydroMapSituations(ms))
    }, [])

    const resetGlobalState = () => {
        setNbMois(nbMoisDefault)
        setErrMode(errModeDefault)
        setNbAnnee(nbAnneeDefault)
        setQuantileCalculation(quantileCalculationDefault)
        setDroughtCalculation(droughtCalculationDefault)
        setMode(modeDefault)
        setDayFormat(dayFormatDefault)
        setModel(modelDefault)
    }

    useEffect(() => {
        resetGlobalState()
    }, [nbMoisDefault, errModeDefault, nbAnneeDefault, quantileCalculationDefault,
        droughtCalculationDefault, modeDefault, dayFormatDefault, modelDefault])

    const onCancel = () => {
        dispatch(AdministrationAction.fetchApplicationSettings()).then(() => {
            resetGlobalState()
            setReadMode(true)
        })
    }

    useTitle(() => [{
        title: i18n.steering,
        href: 'territory',
    }, {
        title: i18n.settings,
        href: 'territory/settings',
    }, {
        title: i18n.parameters,
        href: 'territory/settings/parameters',
    }], [])

    useActions(() => {
        if (readMode) {
            return { edit: () => setReadMode(false) }
        }
        return {
            cancel: () => onCancel(),
            save: () => {
                const settings = [
                    { parameter: 'usagesInTerritoryCalcul', value: usagesInTerritoryCalcul.reduce((acc, i) => `${acc} ${i}`, '') },
                    { parameter: 'nbRotationsCultMax', value: hasValue(nbRotationsCultMax) ? String(nbRotationsCultMax) : '6' },
                    { parameter: 'lowWaterStartDate', value: hasValue(lowWaterStartDate) ? String(lowWaterStartDate) : null },
                    { parameter: 'lowWaterEndDate', value: hasValue(lowWaterEndDate) ? String(lowWaterEndDate) : null },
                    { parameter: 'materielsTypeForIndex', value: `${materielsTypeForIndex}` },
                    { parameter: 'entryFrequency', value: hasValue(entryFrequency) ? String(entryFrequency) : null },
                    { parameter: 'entryFrequencyDay', value: hasValue(entryFrequencyDay) ? String(entryFrequencyDay) : null },
                    { parameter: 'entryRangeDaysBefore', value: hasValue(entryRangeDaysBefore) ? String(entryRangeDaysBefore) : null },
                    { parameter: 'entryRangeDaysAfter', value: hasValue(entryRangeDaysAfter) ? String(entryRangeDaysAfter) : null },
                    { parameter: 'blockConsumptionsDeletion', value: blockConsumptionsDeletion ? '1' : null },
                    { parameter: 'blockEntryDate', value: blockEntryDate ? '1' : null },
                    { parameter: 'blockUnvalidEntry', value: blockUnvalidEntry ? '1' : null },
                    { parameter: 'showClimateControlScenarioPanel', value: showClimateControlScenarioPanel ? '1' : null },
                    { parameter: 'A3PSituationCardPiezo', value: hasValue(situationCardPiezo) ? intParser(situationCardPiezo) : null },
                    { parameter: 'A3PSituationCardHydro', value: hasValue(situationCardHydro) ? intParser(situationCardHydro) : null },
                    { parameter: 'restrictedVPLevelLabel', value: hasValue(restrictedVPLevelLabel) ? String(restrictedVPLevelLabel) : i18n.VPRestricted },
                    { parameter: 'restrictedVPLevelValue', value: hasValue(restrictedVPLevelValue) ? intParser(restrictedVPLevelValue) : null },
                    { parameter: 'normalVPLevelLabel', value: hasValue(normalVPLevelLabel) ? String(normalVPLevelLabel) : i18n.VPNormal },
                    { parameter: 'normalVPLevelValue', value: hasValue(normalVPLevelValue) ? intParser(normalVPLevelValue) : null },
                    { parameter: 'highVPLevelLabel', value: hasValue(highVPLevelLabel) ? String(highVPLevelLabel) : i18n.VPHigh },
                    { parameter: 'highVPLevelValue', value: hasValue(highVPLevelValue) ? intParser(highVPLevelValue) : null },
                    { parameter: 'veryHighVPLevelLabel', value: hasValue(veryHighVPLevelLabel) ? String(veryHighVPLevelLabel) : i18n.VPVeryHigh },
                    { parameter: 'veryHighVPLevelValue', value: hasValue(veryHighVPLevelValue) ? intParser(veryHighVPLevelValue) : null },
                ]
                const globalSettings = [
                    { module: 'SIEAU', parameter: 'A3P_nb_mois', value: hasValue(nbMois) ? String(nbMois) : undefined },
                    { module: 'SIEAU', parameter: 'A3P_err_mode', value: hasValue(errMode) ? String(errMode) : undefined },
                    { module: 'SIEAU', parameter: 'A3P_nb_annee', value: hasValue(nbAnnee) ? String(nbAnnee) : undefined },
                    { module: 'SIEAU', parameter: 'A3P_quantile_calculation', value: hasValue(quantileCalculation) ? String(quantileCalculation) : undefined },
                    { module: 'SIEAU', parameter: 'A3P_drought_calculation', value: hasValue(droughtCalculation) ? String(droughtCalculation) : undefined },
                    { module: 'SIEAU', parameter: 'A3P_mode', value: hasValue(mode) ? String(mode) : undefined },
                    { module: 'SIEAU', parameter: 'A3P_day_format', value: hasValue(dayFormat) ? String(dayFormat) : undefined },
                    { module: 'SIEAU', parameter: 'A3P_model', value: hasValue(model) ? String(model) : undefined },
                ]
                dispatch(AdministrationAction.updateSieauParameters(settings)).then(() => {
                    dispatch(AdministrationAction.fetchApplicationSettings()).then(() => {
                        dispatch(AdministrationAction.updateGlobalParameters(globalSettings)).then(() => {
                            dispatch(AdministrationAction.fetchGlobalParameters()).then(() => {
                                setReadMode(true)
                            })
                        })
                    })
                })
            },
        }
    }, [readMode, usagesInTerritoryCalcul, nbRotationsCultMax, lowWaterStartDate, lowWaterEndDate, materielsTypeForIndex,
        entryFrequency, entryFrequencyDay, entryRangeDaysBefore, entryRangeDaysAfter,
        blockConsumptionsDeletion, blockEntryDate, blockUnvalidEntry, showClimateControlScenarioPanel, situationCardPiezo,
        situationCardHydro, restrictedVPLevelLabel, restrictedVPLevelValue, normalVPLevelLabel, normalVPLevelValue,
        highVPLevelLabel, highVPLevelValue, veryHighVPLevelLabel, veryHighVPLevelValue, nbMois, errMode, nbAnnee,
        quantileCalculation, droughtCalculation, mode, dayFormat, model])

    const onChangeUsages = (id) => {
        if (!usagesInTerritoryCalcul.includes('all')) {
            if (id === 'all') {
                setUsagesInTerritoryCalcul(['all'])
            } else if (usagesInTerritoryCalcul.includes(id)) {
                setUsagesInTerritoryCalcul(usagesInTerritoryCalcul.filter((uId) => uId !== id))
            } else if (isEmpty(xor(usages.map((u) => `${u.id}`), [...usagesInTerritoryCalcul, id]))) {
                setUsagesInTerritoryCalcul(['all'])
            } else {
                setUsagesInTerritoryCalcul([...usagesInTerritoryCalcul, id])
            }
        } else {
            setUsagesInTerritoryCalcul([...usages.map((u) => `${u.id}`), ...usagesInTerritoryCalcul].filter((uId) => uId !== id).filter((uId) => uId !== 'all'))
        }
    }

    const entryDays = useMemo(() => {
        if (entryFrequency === ENTRY_SETTINGS_FREQUENCY.WEEKLY) {
            return getDayList()
        }
        return range(1, 32).map((d) => ({ value: String(d), label: d }))
    }, [entryFrequency])

    if (!isLoaded) {
        return <ProgressCard progress={progress} withMessage round />
    }

    return (
        <Card>
            <CardTitle title={i18n.parameters} />
            <CardContent>
                <Stack>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.usagesForTotalVolumesRequested}</StyledLegend>
                        <Grid container spacing={1}>
                            <SimpleCheckbox
                                label={i18n.everybody}
                                checked={ (usagesInTerritoryCalcul || []).includes('all') }
                                onToggle={() => onChangeUsages('all')}
                                disabled={readMode}
                                data-cy='all'
                            />
                        </Grid>
                        <Grid container spacing={1}>
                            {orderBy(usages, 'description').map((u) => (
                                <SimpleCheckbox
                                    key={u.id}
                                    label={u.description}
                                    checked={(usagesInTerritoryCalcul || []).some(r=> [`${u.id}`, 'all'].includes(r))}
                                    onToggle={() => onChangeUsages(`${u.id}`)}
                                    disabled={readMode}
                                />
                            ))}
                        </Grid>
                    </StyledFieldSet>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.usages}</StyledLegend>
                        <Grid container spacing={1}>
                            <Grid size={3}>
                                <NumberField
                                    title={i18n.nbRotationsCultMax}
                                    value={nbRotationsCultMax}
                                    onChange={(v) => setNbRotationsCultMax(v)}
                                    disabled={readMode}
                                    min={1}
                                    data-cy='nbRotationsCultMax'
                                />
                            </Grid>
                        </Grid>
                    </StyledFieldSet>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.lowWater}</StyledLegend>
                        <Grid container spacing={1}>
                            <Grid size={3}>
                                <SimpleDatePicker
                                    label={i18n.startDate}
                                    value={lowWaterStartDate}
                                    onChange={(v) => setLowWaterStartDate(v)}
                                    disabled={readMode}
                                    max={lowWaterEndDate}
                                    data-cy='startDate'
                                />
                            </Grid>
                            <Grid size={3}>
                                <SimpleDatePicker
                                    label={i18n.endDate}
                                    value={lowWaterEndDate}
                                    onChange={(v) => setLowWaterEndDate(v)}
                                    disabled={readMode}
                                    min={lowWaterStartDate}
                                    data-cy='endDate'
                                />
                            </Grid>
                        </Grid>
                    </StyledFieldSet>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.entry}</StyledLegend>
                        <Grid container spacing={1}>
                            <Grid size={6}>
                                <SuperMultiAutocomplete
                                    label={i18n.materielsForIndex}
                                    options={orderBy(variousMaterielTypes, ['category', 'name'])}
                                    onChange={values => setMaterielsTypeForIndex(values)}
                                    values={materielsTypeForIndex}
                                    multiple
                                    limit={3}
                                    disabled={readMode}
                                    data-cy='materielsForIndex'
                                />
                            </Grid>
                            <Grid container size={12}>
                                <SimpleCheckbox
                                    label={i18n.blockConsumptionsDeletion}
                                    checked={blockConsumptionsDeletion}
                                    onToggle={() => setBlockConsumptionsDeletion(prev => !prev)}
                                    disabled={readMode}
                                    tooltip={i18n.blockConsumptionsDeletionDescr}
                                    data-cy='blockConsumptionsDeletion'
                                />
                                <SimpleCheckbox
                                    label={i18n.blockEntryDateToTodayDate}
                                    checked={blockEntryDate}
                                    onToggle={() => setBlockEntryDate(prev => !prev)}
                                    disabled={readMode}
                                    tooltip={i18n.blockEntryDateToTodayDateDescr}
                                    data-cy='blockEntryDate'
                                />
                                <SimpleCheckbox
                                    label={i18n.blockUnvalidEntry}
                                    checked={blockUnvalidEntry}
                                    onToggle={() => setBlockUnvalidEntry(prev => !prev)}
                                    disabled={readMode}
                                    tooltip={i18n.blockUnvalidEntryDescr}
                                    data-cy='blockUnvalidEntry'
                                />
                            </Grid>
                            <Grid size={3}>
                                <Select
                                    label={i18n.frequency}
                                    value={entryFrequency}
                                    onChange={v => {
                                        setEntryFrequency(v)
                                        setEntryFrequencyDay()
                                        setEntryRangeDaysBefore(0)
                                        setEntryRangeDaysAfter(0)
                                    }}
                                    clearFunction
                                    integerValue
                                    options={getEntryFrequencyList()}
                                    disabled={readMode}
                                    id='frequency'
                                    data-cy='frequency'
                                />
                            </Grid>
                            <Grid size={3}>
                                <Select
                                    label={i18n.day}
                                    value={entryFrequencyDay}
                                    onChange={v => setEntryFrequencyDay(v)}
                                    clearFunction
                                    integerValue
                                    options={entryDays}
                                    disabled={readMode || isNil(entryFrequency)}
                                    noSort
                                    id='day'
                                    data-cy='day'
                                />
                            </Grid>
                            {!isNil(entryFrequency) && !isNil(entryFrequencyDay) && (
                                <TerritorySettingsEntryRange
                                    frequency={entryFrequency}
                                    referenceDay={entryFrequencyDay}
                                    daysBefore={entryRangeDaysBefore}
                                    daysAfter={entryRangeDaysAfter}
                                    onChange={({ daysBefore, daysAfter }) => {
                                        setEntryRangeDaysBefore(daysBefore)
                                        setEntryRangeDaysAfter(daysAfter)
                                    }}
                                    disabled={readMode}
                                />
                            )}
                        </Grid>
                    </StyledFieldSet>
                    {(usedUrl === 'https://recette.manager.capdl.irrigeau.fr/' ||
                    usedUrl === 'https://manager.irrigeau-pays-de-la-loire.chambres-agriculture.fr/') && (
                        <StyledFieldSet>
                            <StyledLegend>{i18n.A3P}</StyledLegend>
                            <Stack spacing={1}>
                                <Grid container>
                                    <SimpleCheckbox
                                        label={i18n.showClimateControlScenarioPanel}
                                        checked={showClimateControlScenarioPanel}
                                        onToggle={() => setShowClimateControlScenarioPanel(prev => !prev)}
                                        disabled={readMode}
                                    />
                                </Grid>
                                <StyledFieldSet>
                                    <StyledLegend>{i18n.mapSituations}</StyledLegend>
                                    <Stack direction={'row'} spacing={1}>
                                        <Select
                                            label={i18n.piezometry}
                                            options={orderBy(piezoMapSituations, 'name')}
                                            keyValue='id'
                                            keyLabel='name'
                                            value={situationCardPiezo}
                                            onChange={newSituationCard => setSituationCardPiezo(newSituationCard)}
                                            integerValue
                                            clearFunction
                                            disabled={readMode}
                                        />
                                        <Select
                                            label={i18n.hydrometry}
                                            options={orderBy(hydroMapSituations, 'name')}
                                            keyValue='id'
                                            keyLabel='name'
                                            value={situationCardHydro}
                                            onChange={newSituationCard => setSituationCardHydro(newSituationCard)}
                                            integerValue
                                            clearFunction
                                            disabled={readMode}
                                        />
                                    </Stack>
                                </StyledFieldSet>
                                <StyledFieldSet>
                                    <StyledLegend>{i18n.VPLevel}</StyledLegend>
                                    <Stack spacing={1}>
                                        <Grid container spacing={1}>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title={i18n.VPRestricted}
                                                    type={INPUT_TYPES.text}
                                                    disabled={readMode}
                                                    value={restrictedVPLevelLabel}
                                                    onChange={(v) => setRestrictedVPLevelLabel(v)}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title={i18n.value}
                                                    type={INPUT_TYPES.int}
                                                    disabled={readMode}
                                                    value={restrictedVPLevelValue}
                                                    onChange={(v) => setRestrictedVPLevelValue(v)}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={1}>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title={i18n.VPNormal}
                                                    type={INPUT_TYPES.text}
                                                    disabled={readMode}
                                                    value={normalVPLevelLabel}
                                                    onChange={(v) => setNormalVPLevelLabel(v)}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title={i18n.value}
                                                    type={INPUT_TYPES.int}
                                                    disabled={readMode}
                                                    value={normalVPLevelValue}
                                                    onChange={(v) => setNormalVPLevelValue(v)}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={1}>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title={i18n.VPHigh}
                                                    type={INPUT_TYPES.text}
                                                    disabled={readMode}
                                                    value={highVPLevelLabel}
                                                    onChange={(v) => setHighVPLevelLabel(v)}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title={i18n.value}
                                                    type={INPUT_TYPES.int}
                                                    disabled={readMode}
                                                    value={highVPLevelValue}
                                                    onChange={(v) => setHighVPLevelValue(v)}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={1}>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title={i18n.VPVeryHigh}
                                                    type={INPUT_TYPES.text}
                                                    disabled={readMode}
                                                    value={veryHighVPLevelLabel}
                                                    onChange={(v) => setVeryHighVPLevelLabel(v)}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title={i18n.value}
                                                    type={INPUT_TYPES.int}
                                                    disabled={readMode}
                                                    value={veryHighVPLevelValue}
                                                    onChange={(v) => setVeryHighVPLevelValue(v)}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                </StyledFieldSet>
                                <StyledFieldSet>
                                    <StyledLegend>{i18n.hydrologicalModel}</StyledLegend>
                                    <Grid container spacing={1}>
                                        <Grid size={3}>
                                            <Select
                                                label={i18n.model}
                                                disabled={readMode}
                                                options={MODEL_OPTIONS}
                                                value={model}
                                                onChange={setModel}
                                            />
                                        </Grid>
                                    </Grid>
                                </StyledFieldSet>
                                <StyledFieldSet>
                                    <StyledLegend>
                                        <Grid container spacing={1}>
                                            <Grid
                                                onClick={() => setSPIHackClick(prev => prev + 1)}
                                            >
                                                {i18n.droughtIndex}
                                            </Grid>
                                            <Grid
                                                sx={{ cursor: 'pointer' }}
                                                onClick={(event) => setAnchorPopover(event.currentTarget)}
                                            >
                                                <InfoOutlinedIcon />
                                            </Grid>
                                            <Popover
                                                open={!!anchorPopover}
                                                anchorEl={anchorPopover}
                                                onClose={(event) => {
                                                    event.stopPropagation()
                                                    setAnchorPopover(null)
                                                }}
                                                onClick={(event) => event.stopPropagation()}
                                                anchorOrigin={{
                                                    vertical: 'center',
                                                    horizontal: 'right',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'center',
                                                    horizontal: 'left',
                                                }}
                                                slotProps={{
                                                    paper: {
                                                        sx: {
                                                            width: '450px',
                                                            padding: '0.5rem',
                                                        },
                                                    },
                                                }}
                                            >
                                                {i18n.droughtIndexExplainationText}
                                            </Popover>
                                        </Grid>
                                    </StyledLegend>
                                    <Grid container spacing={1}>
                                        <Grid size={2}>
                                            <SimpleInput
                                                title={i18n.numberOfMonths}
                                                type={INPUT_TYPES.int}
                                                disabled={readMode}
                                                value={nbMois}
                                                onChange={(v) => setNbMois(v)}
                                            />
                                        </Grid>
                                        {SPIHackClick >= 5 && <Grid size={3}>
                                            <SimpleInput
                                                title='err_mode'
                                                disabled={readMode}
                                                value={errMode}
                                                onChange={(v) => setErrMode(v)}
                                            />
                                        </Grid>}
                                    </Grid>
                                </StyledFieldSet>
                                <StyledFieldSet>
                                    <StyledLegend onClick={() => setKCHackClick(prev => prev + 1)}>
                                        {i18n.agronomicModel}
                                    </StyledLegend>
                                    <Grid container spacing={1}>
                                        <Grid size={2}>
                                            <SimpleInput
                                                title={i18n.historicalClimateDataRequired}
                                                type={INPUT_TYPES.int}
                                                disabled={readMode}
                                                value={nbAnnee}
                                                onChange={(v) => setNbAnnee(v)}
                                            />
                                        </Grid>
                                        {KCHackClick >= 5 && <>
                                            <Grid size={2}>
                                                <SimpleInput
                                                    title='quantile_calculation'
                                                    type={INPUT_TYPES.float}
                                                    disabled={readMode}
                                                    value={quantileCalculation}
                                                    onChange={(v) => setQuantileCalculation(v)}
                                                    floatValue
                                                />
                                            </Grid>
                                            <Grid size={8}/>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title='drought_calculation'
                                                    disabled={readMode}
                                                    value={droughtCalculation}
                                                    onChange={(v) => setDroughtCalculation(v)}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title='mode'
                                                    disabled={readMode}
                                                    value={mode}
                                                    onChange={(v) => setMode(v)}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <SimpleInput
                                                    title='day_format'
                                                    disabled={readMode}
                                                    value={dayFormat}
                                                    onChange={(v) => setDayFormat(v)}
                                                />
                                            </Grid>
                                        </>}
                                    </Grid>
                                </StyledFieldSet>
                            </Stack>
                        </StyledFieldSet>
                    )}
                </Stack>
            </CardContent>
        </Card>
    )
}

TerritorySettingsParamsApp.propTypes = {
    params: PropTypes.shape({
        id: PropTypes.string,
    }),
    usages: PropTypes.arrayOf(PropTypes.instanceOf(DtoUsageAgri)),
    variousMaterielTypes: PropTypes.arrayOf(PropTypes.instanceOf(DtoVariousMaterielType)),
    fetchVariousMaterielTypes: PropTypes.func,
}

export default TerritorySettingsParamsApp